"use client"

/**
 * Blog Engagement Tracking Hook
 *
 * Tracks user engagement with blog articles:
 * - Page view on mount (blogArticleViewed)
 * - Scroll milestones (25%, 50%, 75%, 100%)
 * - Time on page (accumulated)
 * - Final read metrics on unload (blogArticleRead)
 *
 * All events are sent to both GA4 and PostHog via contentAnalytics.
 */

import { useEffect, useRef, useCallback } from 'react'
import { contentAnalytics, trackEvent, runWhenPageVisible } from '@/lib/analytics'

interface UseBlogTrackingOptions {
  /** Article slug for tracking */
  articleSlug: string
  /** Category of the article */
  category: string
  /** Estimated reading time in minutes */
  readingTimeEstimate: number
  /** Whether tracking is enabled (default: true) */
  enabled?: boolean
}

interface ScrollMilestones {
  25: boolean
  50: boolean
  75: boolean
  100: boolean
}

export function useBlogTracking({
  articleSlug,
  category,
  readingTimeEstimate,
  enabled = true
}: UseBlogTrackingOptions) {
  // Track which milestones have been fired
  const milestonesRef = useRef<ScrollMilestones>({
    25: false,
    50: false,
    75: false,
    100: false
  })

  // Track time on page
  const startTimeRef = useRef<number>(0)
  const accumulatedTimeRef = useRef<number>(0)
  const isVisibleRef = useRef<boolean>(true)
  const lastVisibilityChangeRef = useRef<number>(0)

  // Track max scroll depth
  const maxScrollDepthRef = useRef<number>(0)

  // Track if page view was fired
  const pageViewFiredRef = useRef<boolean>(false)

  // Track if the final read event was fired. One exit can trigger up to three
  // of beforeunload / pagehide / unmount-cleanup; only the first one counts.
  const readFiredRef = useRef<boolean>(false)

  // Calculate current scroll depth
  const getScrollDepth = useCallback((): number => {
    if (typeof window === 'undefined') return 0

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    if (scrollHeight <= 0) return 100

    const scrollTop = window.scrollY
    return Math.min(100, Math.round((scrollTop / scrollHeight) * 100))
  }, [])

  // Fire milestone event
  const fireMilestone = useCallback(
    (milestone: keyof ScrollMilestones) => {
      if (!enabled || milestonesRef.current[milestone]) return
      // No milestones before the view event: a hidden/prerendered page can
      // report scrollHeight <= 0 (depth 100) and spray phantom milestones.
      if (!pageViewFiredRef.current) return

      milestonesRef.current[milestone] = true

      // Track scroll milestone as a custom event. Dual-pipes via trackEvent
      // so PostHog can slice funnels by scroll depth alongside GA.
      trackEvent('scroll_milestone', {
        article_slug: articleSlug,
        milestone_percent: milestone,
        category,
      })

      // Also log for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[BlogTracking] Scroll milestone: ${milestone}%`, { articleSlug })
      }
    },
    [articleSlug, category, enabled]
  )

  // Get total time on page (accounting for visibility)
  const getTotalTimeOnPage = useCallback((): number => {
    if (!isVisibleRef.current) {
      return accumulatedTimeRef.current
    }
    const currentSessionTime = Date.now() - lastVisibilityChangeRef.current
    return Math.round((accumulatedTimeRef.current + currentSessionTime) / 1000)
  }, [])

  // Fire final read event
  const fireReadEvent = useCallback(() => {
    if (!enabled) return
    // A page closed without ever being foregrounded (background tab, discarded
    // prerender) was never read; its timing refs are also uninitialized.
    if (!pageViewFiredRef.current) return
    if (readFiredRef.current) return
    readFiredRef.current = true

    const timeOnPage = getTotalTimeOnPage()
    const scrollDepth = maxScrollDepthRef.current

    contentAnalytics.blogArticleRead(articleSlug, timeOnPage, scrollDepth)

    if (process.env.NODE_ENV === 'development') {
      console.log('[BlogTracking] Article read event fired', {
        articleSlug,
        timeOnPage,
        scrollDepth
      })
    }
  }, [articleSlug, enabled, getTotalTimeOnPage])

  // Reset per-article state if this hook instance survives a navigation
  // between posts. Today /blog/[slug] remounts per route, but the tracker
  // must not silently depend on that. Defined FIRST so on a slug change it
  // runs before the other effects' setups — and crucially in the setup phase,
  // after the previous article's effect cleanups have fired its read event
  // with the old closures.
  useEffect(() => {
    milestonesRef.current = { 25: false, 50: false, 75: false, 100: false }
    maxScrollDepthRef.current = 0
    accumulatedTimeRef.current = 0
    startTimeRef.current = 0
    lastVisibilityChangeRef.current = 0
    isVisibleRef.current = true
    pageViewFiredRef.current = false
    readFiredRef.current = false
  }, [articleSlug])

  // Handle scroll events
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const handleScroll = () => {
      const depth = getScrollDepth()

      // Update max scroll depth
      if (depth > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = depth
      }

      // Check milestones
      if (depth >= 25 && !milestonesRef.current[25]) fireMilestone(25)
      if (depth >= 50 && !milestonesRef.current[50]) fireMilestone(50)
      if (depth >= 75 && !milestonesRef.current[75]) fireMilestone(75)
      if (depth >= 100 && !milestonesRef.current[100]) fireMilestone(100)
    }

    // Throttle scroll handler
    let ticking = false
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledScroll, { passive: true })

    // Check initial scroll position (in case page loads partway down)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', throttledScroll)
    }
  }, [enabled, getScrollDepth, fireMilestone])

  // Handle visibility changes (pause timer when tab hidden)
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - accumulate time
        const sessionTime = Date.now() - lastVisibilityChangeRef.current
        accumulatedTimeRef.current += sessionTime
        isVisibleRef.current = false
      } else {
        // Tab visible - restart timer
        lastVisibilityChangeRef.current = Date.now()
        isVisibleRef.current = true
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled])

  // Fire page view on mount
  useEffect(() => {
    if (!enabled || pageViewFiredRef.current) return

    // Defer until the page is actually visible so background-tab opens and
    // SERP prerenders don't emit article views into pageview-less sessions
    // (posthog-js holds the initial $pageview the same way).
    return runWhenPageVisible(() => {
      if (pageViewFiredRef.current) return

      // Initialize timing from the moment of visibility, not mount
      startTimeRef.current = Date.now()
      lastVisibilityChangeRef.current = Date.now()
      isVisibleRef.current = !document.hidden

      // Fire page view event
      contentAnalytics.blogArticleViewed(articleSlug, category, readingTimeEstimate)
      pageViewFiredRef.current = true

      // Re-check the landing scroll position now that milestones are
      // unlocked — the scroll effect's initial check ran before the view
      // event and bailed, so anchor-link landers would otherwise lose their
      // initial milestone until the next scroll.
      const depth = getScrollDepth()
      if (depth > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = depth
      }
      if (depth >= 25) fireMilestone(25)
      if (depth >= 50) fireMilestone(50)
      if (depth >= 75) fireMilestone(75)
      if (depth >= 100) fireMilestone(100)

      if (process.env.NODE_ENV === 'development') {
        console.log('[BlogTracking] Article viewed event fired', {
          articleSlug,
          category,
          readingTimeEstimate
        })
      }
    })
  }, [articleSlug, category, readingTimeEstimate, enabled, getScrollDepth, fireMilestone])

  // Fire read event on unload
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const handleBeforeUnload = () => {
      fireReadEvent()
    }

    // Use both beforeunload and pagehide for better coverage
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handleBeforeUnload)
      // Also fire on cleanup (route change)
      fireReadEvent()
    }
  }, [enabled, fireReadEvent])

  // Return utilities for manual tracking if needed
  return {
    /** Current scroll depth percentage */
    getScrollDepth,
    /** Total time on page in seconds */
    getTimeOnPage: getTotalTimeOnPage,
    /** Manually fire read event (useful for SPA navigation) */
    fireReadEvent,
    /** Check if a milestone has been reached */
    hasMilestone: (milestone: keyof ScrollMilestones) => milestonesRef.current[milestone]
  }
}
